import { Dialog, Transition } from "@headlessui/react";
import React, { Fragment, useEffect, useRef, useState } from "react";
import CloseIcon from "../../../../assets/icons/close-icon";
import UploadIcon from "../../../../assets/icons/upload-icon";
import Papa from "papaparse";
import errorToast from "../../../helper-components/error-toast";
import { Form, Formik } from "formik";
import AsyncCreateSelect from "../../../forms/async-create-select-field";
import { customerListAction } from "../../../../redux/actions/general-action";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import SelectField from "../../../forms/select-field";
import * as Yup from "yup";
import { ChevronDown } from "lucide-react";

import {
  templateCreateAction,
  templateEditAction,
  templateViewAction,
} from "../../../../redux/actions/settings-action";
import successToast from "../../../helper-components/success-toast";
import LoadingSpinner from "../../../helper-components/loading-spinner";
import TemplateAddOptModal from "./template-additional-option-modal";
import TemplateFormatPreview from "./template-format-preview";

// Column picker for the Map columns step. A native <select> can't lay out an
// option, so this shows the column on the left and, in small grey text on the
// right, the field already mapped to it. The list is position: fixed so the
// scrolling panels don't clip it; the modal is CSS-transformed, which makes
// "fixed" relative to the modal, so its offset is taken off.
const ColumnSelect = ({
  value,
  onChange,
  options,
  placeholder,
  className = "",
  title = "",
}) => {
  const [pos, setPos] = useState(null);
  const btnRef = useRef(null);
  const listRef = useRef(null);
  const open = !!pos;

  const openList = () => {
    const btn = btnRef.current;
    if (!btn) return;
    const r = btn.getBoundingClientRect();
    let origin = { top: 0, left: 0 };
    for (let el = btn.parentElement; el; el = el.parentElement) {
      if (getComputedStyle(el).transform !== "none") {
        const o = el.getBoundingClientRect();
        origin = { top: o.top + el.clientTop, left: o.left + el.clientLeft };
        break;
      }
    }
    const below = window.innerHeight - r.bottom - 8;
    const above = r.top - 8;
    const up = below < 240 && above > below;
    const maxHeight = Math.min(288, up ? above : below);
    setPos({
      left: r.left - origin.left,
      width: r.width,
      maxHeight,
      top: (up ? r.top - maxHeight - 4 : r.bottom + 4) - origin.top,
    });
  };

  useEffect(() => {
    if (!open) return;
    const outside = (e) => {
      if (
        btnRef.current?.contains(e.target) ||
        listRef.current?.contains(e.target)
      )
        return;
      setPos(null);
    };
    // Close when anything but the list itself scrolls - it would drift.
    const onScroll = (e) => {
      if (!listRef.current?.contains(e.target)) setPos(null);
    };
    const close = () => setPos(null);
    document.addEventListener("mousedown", outside);
    window.addEventListener("scroll", onScroll, true);
    window.addEventListener("resize", close);
    // Bring the selected option into view by scrolling the list only -
    // scrollIntoView can also scroll the panel behind it, which would fire
    // onScroll above and close the list straight away.
    const list = listRef.current;
    const sel = list?.querySelector('[data-selected="true"]');
    if (list && sel) {
      list.scrollTop = sel.offsetTop - list.clientHeight / 2 + sel.offsetHeight / 2;
    }
    return () => {
      document.removeEventListener("mousedown", outside);
      window.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("resize", close);
    };
  }, [open]);

  const selected = options.find((o) => o.value === value);

  const pick = (v) => {
    onChange(v);
    setPos(null);
    btnRef.current?.focus();
  };

  return (
    <div className="flex-1 min-w-0">
      <button
        ref={btnRef}
        type="button"
        title={title}
        onClick={() => (open ? setPos(null) : openList())}
        onKeyDown={(e) => {
          // Escape closes the list only, not the whole modal.
          if (e.key === "Escape" && open) {
            e.preventDefault();
            e.stopPropagation();
            setPos(null);
          }
        }}
        className={`w-full h-9 px-2 flex items-center gap-2 rounded-lg border bg-white text-sm text-left focus:outline-none ${className}`}
      >
        <span className="flex-1 min-w-0 truncate">
          {selected ? selected.label : placeholder}
        </span>
        <ChevronDown
          size={16}
          className={`shrink-0 text-[var(--description-color)] transition-transform ${
            open ? "rotate-180" : ""
          }`}
        />
      </button>
      {open && (
        <div
          ref={listRef}
          style={{
            position: "fixed",
            top: pos.top,
            left: pos.left,
            width: pos.width,
            maxHeight: pos.maxHeight,
          }}
          className="z-[60] overflow-auto py-1 rounded-lg border border-[var(--light-border-color)] bg-white shadow-lg"
        >
          {[{ value: "", label: placeholder }, ...options].map((o) => {
            const isSelected = o.value === (value || "");
            return (
              <button
                key={o.value}
                type="button"
                data-selected={isSelected}
                onClick={() => pick(o.value)}
                className={`w-full px-3 py-1.5 flex items-center gap-3 text-sm text-left ${
                  isSelected
                    ? "bg-[var(--row-selection-color)] text-[var(--primary)]"
                    : `hover:bg-[var(--row-hover-color)] ${
                        o.value === ""
                          ? "text-[var(--description-color)]"
                          : "text-[var(--heading-color)]"
                      }`
                }`}
              >
                <span className="flex-1 min-w-0 truncate">{o.label}</span>
                {o.tag && (
                  <span className="shrink-0 max-w-[45%] truncate text-xs text-[var(--description-color)]">
                    {o.tag}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

const TemplateUploadModal = ({
  isModalOpen = false,
  setIsModalOpen,
  mapping_id = "",
  setMapping_id,
  reload,
  setReload,
}) => {
  const [templateData, setTemplateData] = useState({});
  const formikRef = useRef(null); // Create ref for Formik
  const [isPreviewOpen, setisPreviewOpen] = useState(false);
  const [format, setFormat] = useState({
    gst_format: templateData?.gst_format || "gst",
    exp_format: templateData?.exp_format || "single",
  });
  const [loading, setLoading] = useState(false);
  const [isAddOptOpen, setIsAddOptOpen] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [formValues, setFormValues] = useState({});
  const navigate = useNavigate();
  const [localData, setLocalData] = useState([]);
  const dispatch = useDispatch();
  useEffect(() => {
    if (mapping_id && isModalOpen) {
      setLoading(true);
      dispatch(templateViewAction(mapping_id, onSuccessView, onErrorView));
    }
  }, [mapping_id, isModalOpen]);

  const onSuccessView = (data) => {
    setTemplateData(data?.data?.[0]);
    console.log(data, "dsfdssdfsdfsdfsdfasd");
    setLoading(false);
  };
  const onErrorView = (data) => {
    setLoading(false);
  };

  console.log(getGrnColumns(format), "dffddsfdsfdsfdsfsfsfsfsfs");
  const initial = {
    gst_format: "gst",
    exp_format: "single",
    mapping_id: "",
    filename: "",
    cmr_vendor_name: "",
    cmr_vendor_id: "",
    excel_rows: [],
    excel_columns: [],
    mapped_columns: [],
    module_name: "purchase",
    grnColumns: getGrnColumns(formValues),
  };
  const validationSchema = Yup.object().shape({
    cmr_vendor_id: Yup.string().required("Vendor is required"),
    cmr_vendor_name: Yup.object().required("Customer name is required"),
  });

  const initialValues = {
    mapping_id: mapping_id || "",
    gst_format: templateData?.gst_format || "gst",
    exp_format: templateData?.exp_format || "single",
    filename: "",
    cmr_vendor_name: templateData?.cmr_vendor_id
      ? {
          label: templateData?.cmr_vendor_name,
          value: templateData?.cmr_vendor_id,
        }
      : "",
    cmr_vendor_id: templateData?.cmr_vendor_id ?? "",
    excel_rows: templateData?.excel_rows || [],
    excel_columns: templateData?.excel_columns || [],
    mapped_columns: templateData?.mapped_columns ?? [],
    module_name: templateData?.module_name || "purchase",
    grnColumns: getGrnColumns(mapping_id ? templateData : initial)?.map(
      (col) => {
        const mappedColumns = templateData?.mapped_columns || {}; // Ensure mapped_columns is an object
        const excelColumns = templateData?.excel_columns || []; // Ensure excel_columns is an array

        const supplierIndex = mappedColumns[col.name]
          ? excelColumns.indexOf(mappedColumns[col.name]) + 1
          : null;

        return { ...col, value: supplierIndex };
      }
    ),
  };

  const handleFileUpload = (event, setValues) => {
    const file = event.target.files[0];
    if (!file) return;
    event.target.value = null; // Reset file input
    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: (results) => {
        const parsedData = results.data;
        if (!parsedData || parsedData.length === 0) {
          errorToast(
            "Error: The CSV file is empty or contains no valid records."
          );
          return;
        }
        setValues({
          ...initial,
          excel_rows: parsedData,
          filename: file?.name,
          excel_columns: Object.keys(parsedData?.[0]),
        });
      },
      error: (error) => {
        errorToast("Error parsing file:", error);
      },
    });
  };

  const loadOptions = (inputValue, field) => {
    return new Promise((resolve) => {
      const onSuccess = (data) => {
        const options = [
          ...data.data.map((item) => ({
            value: item.cmr_id,
            label: item?.cmr_code + "-" + item?.cmr_first_name,
          })),
          {
            value: "new_customer",
            label: <span className="text-primary">+ New Vendor</span>,
          },
        ];
        setLocalData(data.data);
        resolve(options);
      };

      const onError = (error) => {
        resolve([
          {
            value: "new_customer",
            label: <span className="text-primary">+ New Vendor</span>,
          },
        ]);
      };

      if (inputValue?.length > 0) {
        dispatch(
          customerListAction(
            {
              field: field,
              value: inputValue,
              isVendor: true,
            },
            onSuccess,
            onError
          )
        );
      } else {
        resolve([]);
      }
    });
  };

  const onSubmit = (values) => {
    console.log(values, "dfgfdgfdgdfgdfgdfg");
    let actionType = templateCreateAction;
    if (values?.mapping_id) {
      actionType = templateEditAction;
    }
    const { grnColumns, filename, cmr_vendor_name, ...rest } = values;
    dispatch(
      actionType(
        {
          ...rest,
          cmr_vendor_name: cmr_vendor_name?.label,
          mapped_columns: grnColumns.reduce((acc, col) => {
            // Get the corresponding supplier column based on value-1 (since value is 1-based index)
            acc[col.name] = col.value
              ? rest?.excel_columns[col.value - 1] || null
              : null;
            return acc;
          }, {}),
        },
        (data) => {
          successToast(data?.data?.message);
          setMapping_id("");
          setReload(!reload);
          formikRef?.current?.resetForm({ values: initial }); // Reset to initial object
          setTemplateData({});
          setIsModalOpen(false);
        },
        onError
      )
    );
  };

  const onError = (data) => {
    errorToast(data?.data?.message);
  };

  return (
    <Transition show={isModalOpen} as={Fragment}>
      <Dialog
        as="div"
        className="fixed inset-0 z-50 overflow-y-auto"
        onClose={() => {
          setIsModalOpen(false);
          setMapping_id("");
          formikRef?.current?.resetForm({ values: initial }); // Reset to initial object
        }}
      >
        <div className="flex items-center justify-center min-h-screen px-4 text-center">
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-100"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-0"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <Dialog.Overlay className="fixed inset-0 bg-black opacity-60" />
          </Transition.Child>

          {/* This is your modal container */}
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0 translate-y-4"
            enterTo="opacity-100 translate-y-0"
            leave="ease-in duration-0"
            leaveFrom="opacity-100 translate-y-0"
            leaveTo="opacity-0 translate-y-4"
          >
            <div
              className={`mx-auto ${
                formValues?.filename || mapping_id ? "h-[90vh] w-[90%]" : ""
              }  p-5  text-left align-middle transition-all transform bg-white shadow-xl rounded-xl`}
            >
              <Formik
                innerRef={formikRef} // Assign ref to Formik
                // key={initialValues}
                enableReinitialize={true}
                initialValues={initialValues}
                validationSchema={validationSchema}
                onSubmit={onSubmit}
              >
                {({ values, setFieldValue, errors, setValues }) => {
                  setFormValues(values);
                  console.log(values, "values in formik");

                  return (
                    <Form className="h-full w-full ">
                      <TemplateAddOptModal
                        isAddOptOpen={isAddOptOpen}
                        setIsAddOptOpen={setIsAddOptOpen}
                      />
                      {values?.filename || mapping_id ? (
                        <div
                          // Leaves exactly the footer's 4rem below it
                          style={{
                            height: `calc(100% - 4rem)`,
                          }}
                        >
                          <div className="flex justify-between items-start gap-4 h-[3rem]">
                            <div className="flex items-start gap-3 min-w-0">
                              <span className="flex items-center justify-center shrink-0 w-10 h-10 rounded-xl bg-[var(--row-selection-color)]">
                                <UploadIcon
                                  width="20px"
                                  height="20px"
                                  fill="var(--primary)"
                                />
                              </span>
                              <div className="min-w-0">
                                <h2 className="text-base fw_600 text-[var(--heading-color)]">
                                  {mapping_id
                                    ? "Edit import template"
                                    : "Map columns"}
                                </h2>
                                <p className="text-xs mt-0.5 text-[var(--description-color)] truncate">
                                  {values?.filename
                                    ? `${values.filename} · `
                                    : ""}
                                  Pick which column in the vendor's file
                                  holds each system field.
                                </p>
                              </div>
                            </div>
                            {!mapping_id && (
                              <div className="hidden md:flex items-center gap-2 ml-auto mr-2 text-xs whitespace-nowrap">
                                <span className="flex items-center gap-1.5 text-[var(--import-button-fg)]">
                                  <span className="flex items-center justify-center w-5 h-5 rounded-full bg-[var(--import-button-bg)] border border-[var(--import-button-border)] text-[11px]">
                                    ✓
                                  </span>
                                  Upload sample
                                </span>
                                <span className="w-8 h-px bg-[var(--light-border-color)]" />
                                <span className="flex items-center gap-1.5 fw_600 text-[var(--primary)]">
                                  <span className="flex items-center justify-center w-5 h-5 rounded-full bg-[var(--primary)] text-white text-[11px]">
                                    2
                                  </span>
                                  Map columns
                                </span>
                              </div>
                            )}
                            <button
                              type="button"
                              aria-label="Close"
                              className="p-1.5 rounded-lg hover:bg-[var(--row-hover-color)]"
                              onClick={() => {
                                setIsModalOpen(false);
                                setMapping_id("");
                                setValues(initial);
                              }}
                            >
                              <CloseIcon />
                            </button>
                          </div>
                          {loading ? (
                            <LoadingSpinner />
                          ) : (
                            <>
                              <div className="flex justify-between items-center w-full px-4 mt-3 h-[3.5rem] rounded-xl bg-[var(--row-hover-color)] border border-[var(--light-border-color)]">
                                <div className="flex gap-4 items-center">
                                  <div className="w-[30rem] ">
                                    <AsyncCreateSelect
                                      menuPosition=""
                                      menuPortalTarget={document.body}
                                      className={`z-20 ${
                                        errors?.cmr_vendor_name &&
                                        !values?.cmr_vendor_name
                                          ? "selectField_border__error"
                                          : "selectField_border"
                                      }`}
                                      errorClass="hidden"
                                      disabled={false}
                                      minWidth="8.5rem"
                                      marginRight=""
                                      cusType="Vendor"
                                      labelClass="whitespace-nowrap flex text-sm fw_600   gap-2"
                                      type="text"
                                      label="Vendor Name"
                                      autoFocus={true}
                                      name={"cmr_vendor_name"}
                                      placeholder={`Enter Vendor Name`}
                                      onCreateOption={() => {}}
                                      loadOptions={(e) =>
                                        loadOptions(e, "cmr_first_name")
                                      }
                                      onChange={(
                                        selectedOption,
                                        actionMeta
                                      ) => {
                                        if (actionMeta?.action === "clear") {
                                          setFieldValue("cmr_vendor_id", "");
                                          setFieldValue("cmr_vendor_name", "");
                                        } else if (
                                          selectedOption === "new_customer" ||
                                          selectedOption?.value ===
                                            "new_customer"
                                        ) {
                                          navigate("/add-customer");
                                        } else {
                                          const customerData = localData?.find(
                                            (item) =>
                                              item?.cmr_id ===
                                              selectedOption?.value
                                          );

                                          setFieldValue(
                                            "cmr_vendor_id",
                                            customerData?.cmr_id
                                          );
                                          setFieldValue("cmr_vendor_name", {
                                            value: customerData?.cmr_id,
                                            label: customerData?.cmr_first_name,
                                          });
                                        }
                                      }}
                                    />
                                  </div>
                                  <div className="w-[10rem]">
                                    <SelectField
                                      containerClass="z-10"
                                      errMsgContainerClass="hidden"
                                      menuPosition=""
                                      isDisabled={false}
                                      placeholder="Select type"
                                      options={TransTypeOptions}
                                      label=""
                                      id="module_name"
                                      name="module_name"
                                      isSearchable={false}
                                      isRequired={false}
                                      labelWidth="8rem"
                                    />
                                  </div>
                                  <button
                                    type="button"
                                    onClick={() => {
                                      setIsAddOptOpen(true);
                                    }}
                                    className="h-9 px-3 rounded-lg border border-[var(--import-border)] bg-white text-xs fw_600 text-[var(--primary)] hover:bg-[var(--row-selection-color)] whitespace-nowrap"
                                  >
                                    Additional Options
                                  </button>
                                </div>
                              </div>
                              {(() => {
                                const cols = values?.excel_columns || [];
                                const fields = values?.grnColumns || [];
                                const sample = (header) =>
                                  values?.excel_rows?.[0]?.[header];
                                // 1-based column position -> field labels
                                // mapped to it (for the "Mapped to" chips).
                                const mappedTo = {};
                                fields.forEach((f) => {
                                  const pos = Number(f?.value);
                                  if (pos) {
                                    (mappedTo[pos] = mappedTo[pos] || []).push(
                                      f.label
                                    );
                                  }
                                });
                                const required = fields.filter(
                                  (f) => f?.required
                                );
                                const requiredDone = required.filter(
                                  (f) =>
                                    Number(f?.value) > 0 &&
                                    Number(f?.value) <= cols.length
                                ).length;
                                const panel =
                                  "flex flex-col h-full min-h-0 rounded-xl border border-[var(--light-border-color)] overflow-hidden";
                                const panelHead =
                                  "flex items-center justify-between gap-3 px-4 h-[2.75rem] shrink-0 bg-F2F4F5 border-b border-[var(--light-border-color)]";
                                const th =
                                  "sticky top-0 z-[1] px-4 h-[2.25rem] text-left text-[11px] fw_700 uppercase tracking-wide text-475467 bg-white border-b border-[var(--light-border-color)]";
                                const td =
                                  "px-4 py-2.5 text-sm text-[var(--heading-color)] border-b border-[var(--light-border-color)]";

                                return (
                                  <div
                                    className="flex gap-4 mt-3"
                                    // Space above: title 3rem + vendor bar
                                    // 0.75rem gap + 3.5rem + this 0.75rem gap.
                                    // 7rem made the panels 1rem too tall, so
                                    // their bottom border ran under the
                                    // footer's top border.
                                    style={{
                                      height: `calc(100% - 8rem)`,
                                    }}
                                  >
                                    {/* Columns in the vendor file */}
                                    <div className={`${panel} w-[55%]`}>
                                      <div className={panelHead}>
                                        <span className="text-sm fw_600 text-475467">
                                          Columns in the file
                                        </span>
                                        <span className="text-xs text-475467 opacity-80">
                                          {cols.length} columns · sample from
                                          row 1
                                        </span>
                                      </div>
                                      <div className="flex-1 min-h-0 overflow-auto">
                                        <table className="w-full border-collapse">
                                          <thead>
                                            <tr>
                                              <th className={`${th} w-14`}>#</th>
                                              <th className={th}>Header</th>
                                              <th className={th}>Sample</th>
                                              <th className={th}>Mapped to</th>
                                            </tr>
                                          </thead>
                                          <tbody>
                                            {cols.map((header, index) => {
                                              const used =
                                                mappedTo[index + 1] || [];
                                              return (
                                                <tr
                                                  key={index}
                                                  className={
                                                    used.length
                                                      ? "bg-[var(--row-hover-color)]"
                                                      : "hover:bg-[var(--row-hover-color)]"
                                                  }
                                                >
                                                  <td
                                                    className={`${td} text-[var(--description-color)] tabular-nums`}
                                                  >
                                                    {index + 1}
                                                  </td>
                                                  <td className={`${td} fw_500`}>
                                                    {header}
                                                  </td>
                                                  <td
                                                    className={`${td} text-[var(--description-color)] max-w-[12rem] truncate`}
                                                    title={String(
                                                      sample(header) ?? ""
                                                    )}
                                                  >
                                                    {String(
                                                      sample(header) ?? ""
                                                    ).trim() || "—"}
                                                  </td>
                                                  <td className={td}>
                                                    <div className="flex flex-wrap gap-1">
                                                      {used.map((label) => (
                                                        <span
                                                          key={label}
                                                          className={`px-2 py-0.5 rounded-full text-[11px] fw_600 border ${
                                                            used.length > 1
                                                              ? "text-[var(--error-color)] border-[var(--error-color)] bg-white"
                                                              : "text-[var(--primary)] border-[var(--import-border)] bg-white"
                                                          }`}
                                                        >
                                                          {label}
                                                        </span>
                                                      ))}
                                                    </div>
                                                  </td>
                                                </tr>
                                              );
                                            })}
                                          </tbody>
                                        </table>
                                      </div>
                                    </div>

                                    {/* System fields */}
                                    <div className={`${panel} w-[45%]`}>
                                      <div className={panelHead}>
                                        <span className="text-sm fw_600 text-475467">
                                          System fields
                                        </span>
                                        <span
                                          className={`px-2 py-0.5 rounded-full text-[11px] fw_600 border ${
                                            requiredDone === required.length
                                              ? "text-[var(--import-button-fg)] bg-[var(--import-button-bg)] border-[var(--import-button-border)]"
                                              : "text-[var(--error-color)] bg-white border-[var(--error-color)]"
                                          }`}
                                        >
                                          Required {requiredDone}/
                                          {required.length} mapped
                                        </span>
                                      </div>
                                      <div className="flex-1 min-h-0 overflow-auto">
                                        {fields.map((row, index) => {
                                          const pos = Number(row?.value) || "";
                                          const invalid =
                                            pos && pos > cols.length;
                                          const duplicate =
                                            pos &&
                                            (mappedTo[pos] || []).length > 1;
                                          const missing =
                                            row?.required && !pos;
                                          return (
                                            <div
                                              key={index}
                                              className="flex items-center gap-3 px-4 py-2.5 border-b border-[var(--light-border-color)]"
                                            >
                                              <div className="w-[42%] shrink-0 text-sm text-[var(--heading-color)]">
                                                {row?.label}
                                                {row?.required && (
                                                  <span className="text-F51D00">
                                                    {" "}
                                                    *
                                                  </span>
                                                )}
                                              </div>
                                              {/* Column number typed directly -
                                                  same value as the dropdown,
                                                  so the two stay in sync. */}
                                              <input
                                                type="number"
                                                min={1}
                                                max={cols.length}
                                                value={pos}
                                                placeholder="#"
                                                onChange={(e) => {
                                                  const n = parseInt(
                                                    e.target.value,
                                                    10
                                                  );
                                                  setFieldValue(
                                                    `grnColumns[${index}].value`,
                                                    n > 0 ? n : ""
                                                  );
                                                }}
                                                title="Type the column number"
                                                className={`w-[64px] shrink-0 h-9 px-2 rounded-lg border bg-white text-sm text-center text-[var(--heading-color)] focus:outline-none input-field-no-spin-buttons ${
                                                  invalid ||
                                                  duplicate ||
                                                  missing
                                                    ? "border-[var(--error-color)]"
                                                    : pos
                                                    ? "border-[var(--import-border)]"
                                                    : "border-[var(--button-border-color)]"
                                                }`}
                                              />
                                              <ColumnSelect
                                                value={pos}
                                                onChange={(v) =>
                                                  setFieldValue(
                                                    `grnColumns[${index}].value`,
                                                    v ? Number(v) : ""
                                                  )
                                                }
                                                placeholder={
                                                  row?.required
                                                    ? "Select column (required)"
                                                    : "Not in file"
                                                }
                                                className={`${
                                                  invalid ||
                                                  duplicate ||
                                                  missing
                                                    ? "border-[var(--error-color)]"
                                                    : pos
                                                    ? "border-[var(--import-border)]"
                                                    : "border-[var(--button-border-color)]"
                                                } ${
                                                  pos
                                                    ? "text-[var(--heading-color)]"
                                                    : "text-[var(--description-color)]"
                                                }`}
                                                title={
                                                  invalid
                                                    ? "This column isn't in the file"
                                                    : duplicate
                                                    ? "Another field uses this column"
                                                    : ""
                                                }
                                                options={[
                                                  ...(invalid
                                                    ? [
                                                        {
                                                          value: pos,
                                                          label: `#${pos} · not in this file`,
                                                        },
                                                      ]
                                                    : []),
                                                  ...cols.map((header, i) => {
                                                    const s = String(
                                                      sample(header) ?? ""
                                                    ).trim();
                                                    // Field already on this
                                                    // column, as a grey tag -
                                                    // each column maps to one
                                                    // field only.
                                                    const usedBy = (
                                                      mappedTo[i + 1] || []
                                                    ).filter(
                                                      (label) =>
                                                        label !== row?.label
                                                    );
                                                    return {
                                                      value: i + 1,
                                                      label: `#${i + 1} · ${header}${
                                                        s
                                                          ? ` — ${s.slice(0, 24)}`
                                                          : ""
                                                      }`,
                                                      tag: usedBy.join(", "),
                                                    };
                                                  }),
                                                ]}
                                              />
                                            </div>
                                          );
                                        })}
                                      </div>
                                    </div>
                                  </div>
                                );
                              })()}
                            </>
                          )}
                        </div>
                      ) : (
                        <div className="w-[36rem] max-w-full">
                          {/* Header */}
                          <div className="flex items-start justify-between gap-4">
                            <div className="flex items-start gap-3">
                              <span className="flex items-center justify-center shrink-0 w-10 h-10 rounded-xl bg-[var(--row-selection-color)]">
                                <UploadIcon
                                  width="20px"
                                  height="20px"
                                  fill="var(--primary)"
                                />
                              </span>
                              <div>
                                <h2 className="text-base fw_600 text-[var(--heading-color)]">
                                  New import template
                                </h2>
                                <p className="text-xs mt-0.5 text-[var(--description-color)]">
                                  Upload a sample CSV from the vendor. Next,
                                  you'll map its columns to system fields.
                                </p>
                              </div>
                            </div>
                            <button
                              type="button"
                              aria-label="Close"
                              className="p-1.5 rounded-lg hover:bg-[var(--row-hover-color)]"
                              onClick={() => {
                                setIsModalOpen(false);
                                setMapping_id("");
                                setValues(initial);
                              }}
                            >
                              <CloseIcon />
                            </button>
                          </div>

                          {/* Steps */}
                          <div className="flex items-center gap-2 mt-5 text-xs">
                            <span className="flex items-center gap-1.5 fw_600 text-[var(--primary)]">
                              <span className="flex items-center justify-center w-5 h-5 rounded-full bg-[var(--primary)] text-white text-[11px]">
                                1
                              </span>
                              Upload sample
                            </span>
                            <span className="flex-1 max-w-[3rem] h-px bg-[var(--light-border-color)]" />
                            <span className="flex items-center gap-1.5 text-[var(--description-color)]">
                              <span className="flex items-center justify-center w-5 h-5 rounded-full border border-[var(--button-border-color)] text-[11px]">
                                2
                              </span>
                              Map columns
                            </span>
                          </div>

                          {/* Drop zone */}
                          <label
                            htmlFor="template-csv-upload"
                            onDragOver={(e) => {
                              e.preventDefault();
                              setDragOver(true);
                            }}
                            onDragLeave={() => setDragOver(false)}
                            onDrop={(e) => {
                              e.preventDefault();
                              setDragOver(false);
                              const file = e.dataTransfer.files?.[0];
                              if (!file) return;
                              if (!file.name?.toLowerCase().endsWith(".csv")) {
                                errorToast("Please choose a .csv file");
                                return;
                              }
                              handleFileUpload(
                                { target: { files: [file], value: "" } },
                                setValues
                              );
                            }}
                            className={`flex flex-col items-center justify-center gap-2 mt-4 px-4 py-10 rounded-xl border-2 border-dashed border-[var(--import-border)] cursor-pointer transition-colors ${
                              dragOver
                                ? "bg-[var(--row-selection-color)]"
                                : "bg-[var(--row-hover-color)] hover:bg-[var(--row-selection-color)]"
                            }`}
                          >
                            <span className="flex items-center justify-center w-12 h-12 rounded-full bg-white shadow-sm">
                              <UploadIcon
                                width="22px"
                                height="22px"
                                fill="var(--primary)"
                              />
                            </span>
                            <span className="text-sm text-[var(--heading-color)]">
                              Drag &amp; drop the vendor's CSV here, or{" "}
                              <span className="fw_600 underline text-[var(--primary)]">
                                browse
                              </span>
                            </span>
                            <span className="text-xs text-[var(--description-color)]">
                              .csv only · the first row must be the column
                              headers
                            </span>
                            <input
                              type="file"
                              accept=".csv"
                              onChange={(e) => {
                                handleFileUpload(e, setValues, values);
                              }}
                              className="hidden"
                              id="template-csv-upload"
                            />
                          </label>

                          {/* Footer */}
                          <div className="flex justify-end mt-5">
                            <button
                              type="button"
                              onClick={() => {
                                setIsModalOpen(false);
                                setMapping_id("");
                                setValues(initial);
                              }}
                              className="h-10 px-5 rounded-lg border border-[var(--button-border-color)] bg-white text-sm fw_500 text-[var(--heading-color)] hover:bg-[var(--row-hover-color)]"
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      )}

                      {(values?.filename || mapping_id) &&
                        (() => {
                          // Same three checks as before: a position outside
                          // the file, two fields on one column, or a required
                          // field left unmapped.
                          const positions = (values?.grnColumns || [])
                            .map((grn) => grn?.value)
                            .filter(
                              (val) =>
                                val !== "" && val !== null && val !== undefined
                            )
                            .map(Number);
                          const outOfRange = positions.some(
                            (p) => p > (values?.excel_columns?.length || 0)
                          );
                          const hasDuplicates =
                            new Set(positions).size !== positions.length;
                          const missingRequired = (
                            values?.grnColumns || []
                          ).some((field) => field.required && !field.value);
                          const blocked =
                            outOfRange || hasDuplicates || missingRequired;
                          const message = hasDuplicates
                            ? "Two fields point to the same column — each column can be used once."
                            : outOfRange
                            ? "A field points to a column that isn't in this file."
                            : missingRequired
                            ? "Map every required field (*) to save."
                            : "";
                          return (
                            <div className="flex justify-between items-center gap-3 w-full h-[4rem] pt-3 border-t border-[var(--light-border-color)]">
                              <span
                                className={`text-xs ${
                                  message
                                    ? "text-[var(--error-color)] fw_500"
                                    : "text-[var(--description-color)]"
                                }`}
                              >
                                {message || "All required fields are mapped."}
                              </span>
                              <div className="flex items-center gap-2.5">
                                <TemplateFormatPreview
                                  values={values}
                                  isModalOpen={isPreviewOpen}
                                  setIsModalOpen={setisPreviewOpen}
                                />
                                <button
                                  type="button"
                                  onClick={() => {
                                    setIsModalOpen(false);
                                    setMapping_id("");
                                    setValues(initial);
                                  }}
                                  className="h-10 px-5 rounded-lg border border-[var(--button-border-color)] bg-white text-sm fw_500 text-[var(--heading-color)] hover:bg-[var(--row-hover-color)]"
                                >
                                  Cancel
                                </button>
                                <button
                                  type="button"
                                  onClick={() => setisPreviewOpen(true)}
                                  className="h-10 px-5 rounded-lg border border-[var(--import-border)] bg-white text-sm fw_600 text-[var(--primary)] hover:bg-[var(--row-selection-color)]"
                                >
                                  Preview
                                </button>
                                <button
                                  type="submit"
                                  disabled={blocked}
                                  className="h-10 px-6 rounded-lg text-sm fw_600 text-[var(--primary-button-fg-color)] bg-[var(--primary-button-bg-color)] hover:bg-[var(--primary-button-hover-color)] disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                  {mapping_id ? "Save changes" : "Save template"}
                                </button>
                              </div>
                            </div>
                          );
                        })()}
                    </Form>
                  );
                }}
              </Formik>
            </div>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition>
  );
};

export default TemplateUploadModal;

export const TransTypeOptions = [{ value: "purchase", label: "Purchase" }];

export const tableHeading1 = ["Column no.", "Column Header", "Sample Value"];

export const getGrnColumns = (format = {}) => {
  const defaultColumns = [
    {
      label: "Customer Inv Number",
      name: "customer_invoice_number",
      value: "",
      required: true,
    },
    {
      label: "Customer Code",
      name: "cmr_code",
      value: "",
      required: false,
    },
    {
      label: "Customer Name",
      name: "cmr_name",
      value: "",
      required: false,
    },

    {
      label: "Order Date",
      name: "gort_order_date",
      value: "",
      required: true,
    },
    {
      label: "Document Date",
      name: "gort_document_date",
      value: "",
      required: false,
    },
    {
      label: "SubTotal",
      name: "SubTotal",
      value: "",
      required: false,
    },
    {
      label: "Invoice Total Amount",
      name: "total_amount",
      value: "",
      required: true,
    },
    {
      label: "BATCH CGST AMOUNT",
      name: "item_batch_cgst_amount",
      value: "",
      required: false,
    },
    {
      label: "BATCH CGST PERCENTAGE",
      name: "item_batch_cgst_percentage",
      value: "",
      required: false,
    },
    {
      label: "BATCH SGST AMOUNT",
      name: "item_batch_sgst_amount",
      value: "",
      required: false,
    },
    {
      label: "BATCH SGST PERCENTAGE",
      name: "item_batch_sgst_percentage",
      value: "",
      required: false,
    },
    {
      label: "BATCH IGST AMOUNT",
      name: "item_batch_igst_amount",
      value: "",
      required: false,
    },
    {
      label: "BATCH IGST PERCENTAGE",
      name: "item_batch_igst_percentage",
      value: "",
      required: false,
    },
    {
      label: "ITEM BATCH PTR",
      name: "item_batch_ptr_amount",
      value: "",
      required: false,
    },
    {
      label: "ITEM BATCH PTS",
      name: "item_batch_pts_amount",
      value: "",
      required: false,
    },
    {
      label: "Delivery Date",
      name: "gort_delivery_date",
      value: "",
      required: false,
    },

    {
      label: "Payment Status",
      name: "gort_payment_status",
      value: "",
      required: false,
    },
    {
      label: "Transaction ID",
      name: "gort_transaction_id",
      value: "",
      required: false,
    },
    {
      label: "Billing Address",
      name: "gort_billing_address",
      value: "",
      required: false,
    },

    {
      label: "Remarks",
      name: "remarks",
      value: "",
      required: false,
    },

    {
      label: "TCS/TDS Percentage",
      name: "gort_tcs_or_tds_percentage",
      value: "",
      required: false,
    },
    {
      label: "TDS/TCS Type",
      name: "gort_tds_tcs_type",
      value: "",
      required: false,
    },
    {
      label: "TCS/TDS Amount",
      name: "gort_tcs_or_tds_amount",
      value: "",
      required: false,
    },

    {
      label: "Item Name",
      name: "item_name",
      value: "",
      required: true,
    },
    {
      label: "Item Code",
      name: "item_code",
      value: "",
      required: true,
    },
    {
      label: "Item HSN",
      name: "item_hsn",
      value: "",
      required: true,
    },
    {
      label: "Item UOM",
      name: "item_uom",
      value: "",
      required: true,
    },

    {
      label: "Manufacturer Name",
      name: "item_manufacturer_name",
      value: "",
      required: false,
    },
    {
      label: "Batch Number",
      name: "item_batch_number",
      value: "",
      required: true,
    },
    {
      label: "Item Quantity",
      name: "item_sellable_quantity",
      value: "",
      required: true,
    },
    {
      label: "Batch Free Quantity",
      name: "item_batch_free_quantity",
      value: "",
      required: true,
    },

    {
      label: "Batch Purchase Rate",
      name: "item_batch_purchase_rate",
      value: "",
      required: true,
    },
    {
      label: "Batch Total Base Purchase Rate",
      name: "item_batch_total_base_purchase_rate",
      value: "",
      required: false,
    },

    {
      label: "Batch Scheme Discount Percentage",
      name: "item_batch_scheme_discount_percentage",
      value: "",
      required: false,
    },

    {
      label: "Batch Discount Percentage",
      name: "item_batch_discount_percentage",
      value: "",
      required: false,
    },

    {
      label: "Batch Final Purchase Rate",
      name: "item_batch_final_purchase_rate",
      value: "",
      required: false,
    },
    {
      label: "Batch Total Purchase Rate(before tax)",
      name: "item_batch_purhcase_rate_before_tax",
      value: "",
      required: false,
    },
    {
      label: "Batch Total Purchase Rate",
      name: "item_batch_total_purchase_rate",
      value: "",
      required: false,
    },
    {
      label: "MRP",
      name: "item_batch_unit_price",
      value: "",
      required: true,
    },
  ];

  const gstColumns =
    format?.gst_format === "gst"
      ? [{ label: "Item GST % *", name: "item_gst", value: "", required: true }]
      : [
          {
            label: "Item CGST %",
            name: "item_cgst_percentage",
            value: "",
            required: true,
          },
          {
            label: "Item SGST %",
            name: "item_sgst_percentage",
            value: "",
            required: true,
          },
        ];

  const expiryColumns =
    format?.exp_format === "single"
      ? [
          {
            label: "Item Expiry Date",
            name: "item_exp_date",
            value: "",
            required: true,
          },
        ]
      : [
          {
            label: "Expiry Month",
            name: "item_exp_month",
            value: "",
            required: true,
          },
          {
            label: "Expiry year",
            name: "item_exp_year",
            value: "",
            required: true,
          },
        ];

  return [...defaultColumns, ...gstColumns, ...expiryColumns];
};
